import {
  ArrowClockwise,
  CaretDoubleDown,
  CaretDoubleUp,
  Check,
  GitPullRequest,
  X,
} from '@phosphor-icons/react';
import { Button, Flex, TAG_COLORS, TagBadge, Text } from '@chatsdet/ui';

import type { ReviewDecisionsApi } from './useReviewDecisions';

interface ReviewHeaderProps {
  title: string;
  source?: string;
  stale: boolean;
  onExit: () => void;
}

export function ReviewHeader({ title, source, stale, onExit }: ReviewHeaderProps) {
  const bannerCls = stale
    ? 'border-rose-300/70 bg-rose-50/80 dark:border-rose-500/40 dark:bg-rose-950/30'
    : 'border-amber-300/60 bg-amber-50/70 dark:border-amber-500/40 dark:bg-amber-950/30';
  const iconCls = stale
    ? 'bg-rose-200 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300'
    : 'bg-amber-200 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300';

  return (
    <div
      className={`flex items-center gap-3 border-b px-4 py-2.5 backdrop-blur ${bannerCls}`}
    >
      <div className={`flex h-7 w-7 items-center justify-center rounded-full ${iconCls}`}>
        <GitPullRequest size={14} weight="bold" />
      </div>
      <div className="min-w-0 flex-1">
        <Flex align="center" gap="sm" className="min-w-0">
          <Text weight="semibold" className="truncate text-sm">
            {stale ? 'Proposal is stale' : 'Reviewing:'} {!stale && title}
          </Text>
          <TagBadge name="Beta" color="purple" hideDot className="text-[10px]" />
          {!stale && source && (
            <TagBadge name={source} color="blue" hideDot className="text-[10px] capitalize" />
          )}
        </Flex>
        {stale && (
          <Text size="xs" className="mt-0.5 text-rose-800 dark:text-rose-200">
            The workflow was edited after this proposal was created, so it can't be applied
            cleanly. Close and request a new proposal.
          </Text>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onPress={onExit}
        className="w-8 p-0"
        aria-label="Exit review"
      >
        <X size={18} weight="bold" />
      </Button>
    </div>
  );
}

interface ReviewExpandCollapseControlProps {
  hasExpandedRows: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

export function ReviewExpandCollapseControl({
  hasExpandedRows,
  onExpandAll,
  onCollapseAll,
}: ReviewExpandCollapseControlProps) {
  return (
    <div className="absolute right-3 top-2 z-20 rounded-lg border border-border/60 bg-background/90 shadow-sm backdrop-blur-sm">
      <Button
        variant="ghost"
        size="sm"
        onPress={hasExpandedRows ? onCollapseAll : onExpandAll}
        iconLeft={
          hasExpandedRows ? (
            <CaretDoubleUp size={13} weight="bold" />
          ) : (
            <CaretDoubleDown size={13} weight="bold" />
          )
        }
        className="text-muted-foreground"
      >
        {hasExpandedRows ? 'Collapse all' : 'Expand all'}
      </Button>
    </div>
  );
}

interface ReviewActionBarProps {
  stale: boolean;
  decisions: ReviewDecisionsApi;
  onRefresh: () => void;
  onConfirm: () => void;
  confirmDisabled: boolean;
  confirmPending: boolean;
}

export function ReviewActionBar({
  stale,
  decisions,
  onRefresh,
  onConfirm,
  confirmDisabled,
  confirmPending,
}: ReviewActionBarProps) {
  const { accepted: acceptedCount, rejected: rejectedCount, total } = decisions.counts;
  const allRejected = total > 0 && acceptedCount === 0;
  const hasDecisions = !stale && total > 0;

  const confirmLabel = stale
    ? 'Close'
    : confirmPending
      ? allRejected
        ? 'Rejecting…'
        : 'Applying…'
      : allRejected
        ? 'Reject proposal'
        : 'Confirm changes';

  return (
    <div
      role="toolbar"
      aria-label="Review actions"
      className="flex items-center gap-2 border-t border-border/70 bg-background/95 px-4 py-2.5 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] backdrop-blur"
    >
      {hasDecisions && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onPress={decisions.acceptAll}
            isDisabled={decisions.allAccepted}
            iconLeft={<Check size={12} weight="bold" />}
          >
            Accept all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onPress={decisions.rejectAll}
            isDisabled={decisions.allRejected}
            iconLeft={<X size={12} weight="bold" />}
          >
            Reject all
          </Button>
        </div>
      )}

      {stale && (
        <Text size="xs" className="text-muted-foreground">
          Proposal no longer applies to the current draft.
        </Text>
      )}

      <div className="ml-auto flex items-center gap-3">
        {hasDecisions && (
          <div className="flex items-center gap-2 text-[11px] leading-none">
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <span className={`h-1.5 w-1.5 rounded-full ${TAG_COLORS.blue.dot}`} />
              <span className="font-medium text-foreground">{acceptedCount}</span>
              accept
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <span className={`h-1.5 w-1.5 rounded-full ${TAG_COLORS.orange.dot}`} />
              <span className="font-medium text-foreground">{rejectedCount}</span>
              reject
            </span>
          </div>
        )}
        <Button
          variant={allRejected && !stale ? 'destructive' : 'primary'}
          size="sm"
          onPress={stale ? onRefresh : onConfirm}
          isDisabled={!stale && confirmDisabled}
          iconLeft={
            stale ? (
              <ArrowClockwise size={14} weight="bold" />
            ) : allRejected ? (
              <X size={14} weight="bold" />
            ) : (
              <Check size={14} weight="bold" />
            )
          }
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
